import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button, Form, Space, Switch } from "antd";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { useMerchantData } from "../../hooks/useMerchantData";
import { createMerchant, updateMerchant } from "./store/merchantsUserSlice";
import { MerchantBoxProps } from "./types";
import notification from "../../commonComponents/Notification/Notification";
import {
  getMerchantFormConfig,
  MerchantFormData,
  getMerchantType,
  getMerchantLabel,
  getMerchantUserRoleLabel,
  merchantSchema,
  merchantCreateSchema,
  toUserRole,
} from "./constants";
import {
  StyledModal,
  CancelButton,
  SaveButton,
  FormFooter,
  StyledInput,
  StyledTextArea,
  StyledPasswordInput,
  StyledForm,
} from "./styles";

const MerchantBox: React.FC<MerchantBoxProps> = ({
  initialMerchant,
  onSave,
  onCancel,
  fetchMerchants,
  isManagerMode = false,
  isStaffMode = false,
  userRoleType,
  merchantId,
  managerId,
}) => {
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { loading } = useSelector((state: RootState) => state.merchants);
  const { data: merchantData } = useMerchantData();
  const effectiveMerchantId = merchantId ?? merchantData?.id;

  const isEditMode = !!initialMerchant;
  const effectiveType = userRoleType ?? getMerchantType(isManagerMode, isStaffMode);
  const merchantLabel = userRoleType
    ? getMerchantUserRoleLabel(userRoleType)
    : getMerchantLabel(isManagerMode, isStaffMode);

  // Password is only required in edit mode when the "Update Password" toggle is on,
  // and the toggle can flip after mount, so the resolver reads it from a ref on every
  // validation call instead of being fixed at useForm() init time.
  const showPasswordFieldRef = useRef(showPasswordField);
  showPasswordFieldRef.current = showPasswordField;

  const passwordAwareResolver = useMemo(() => {
    // Password required whenever it's on the form: always in Create, only when the toggle is on in Edit.
    const withPasswordResolver = yupResolver(merchantCreateSchema);
    const withoutPasswordResolver = yupResolver(merchantSchema);
    return (values: any, context: any, options: any) => {
      const resolver = !isEditMode || showPasswordFieldRef.current
        ? withPasswordResolver
        : withoutPasswordResolver;
      return resolver(values, context, options);
    };
  }, [isEditMode]);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    clearErrors,
  } = useForm<MerchantFormData>({
    ...(getMerchantFormConfig(initialMerchant) as any),
    resolver: passwordAwareResolver,
  });

  useEffect(() => {
    setShowPasswordField(false);
    if (initialMerchant) {
      reset({
        name: initialMerchant.name,
        username: initialMerchant.username,
        email: initialMerchant.email,
        phone: initialMerchant.phone,
        publicPhone: initialMerchant.publicPhone || '',
        address: initialMerchant.address,
        password: '',
        userRole: toUserRole(initialMerchant.role),
      });
    } else {
      reset({
        name: '',
        username: '',
        email: '',
        phone: '',
        publicPhone: '',
        address: '',
        password: '',
        userRole: undefined,
      });
    }
  }, [initialMerchant, reset]);

  const handlePasswordToggle = (checked: boolean) => {
    setShowPasswordField(checked);
    if (!checked) {
      setValue('password', '', { shouldDirty: false });
      clearErrors('password');
    }
  };

  useEffect(() => {
    setIsUnsavedChanges(isDirty);
  }, [isDirty]);

  const handleSave = async (values: MerchantFormData) => {
    try {
      if (isEditMode && initialMerchant) {
        const payload: any = {
          id: initialMerchant.id,
          name: values.name,
          username: values.username,
          email: values.email,
          phone: values.phone,
          publicPhone: values.publicPhone,
          address: values.address,
        };

        // Add role/userRole if provided and in manager mode
        if (isManagerMode && values.userRole) {
          payload.role = values.userRole;
        }

        // Only include the password when the user explicitly opted to update it
        if (showPasswordField && values.password) {
          payload.password = values.password;
        }

        const result = await dispatch(
          updateMerchant({
            type: effectiveType === "merchant_manager" ? "manager" : effectiveType === "merchant_staff" ? "staff" : effectiveType,
            payload,
          })
        );

        if (updateMerchant.fulfilled.match(result)) {
          notification.success(`${merchantLabel} updated successfully.`);
          fetchMerchants();
          onSave();
        } else {
          const errorMsg =
            typeof result.payload === "string"
              ? result.payload
              : "Update failed";
          notification.error("Update Failed", errorMsg);
        }
      } else {
        // Merchant ID required only when merchant creates a manager
        if (effectiveType === "merchant_manager" && !effectiveMerchantId) {
          notification.error("Validation", "Merchant profile not found.");
          return;
        }
        // Manager ID required only when merchant creates a staff
        if (effectiveType === "merchant_staff" && !managerId) {
          notification.error("Validation", "Please select a manager for this staff user.");
          return;
        }
        const result = await dispatch(
          createMerchant({
            type: effectiveType,
            payload: {
              name: values.name,
              username: values.username,
              email: values.email,
              phone: values.phone,
              publicPhone: values.publicPhone,
              address: values.address,
              password: values.password,
              ...(effectiveMerchantId && { merchantId: effectiveMerchantId }),
              ...(managerId && { managerId }),
            },
          })
        );

        if (createMerchant.fulfilled.match(result)) {
          notification.success(`${merchantLabel} created successfully.`);
          fetchMerchants();
          onSave();
        } else {
          const errorMsg =
            typeof result.payload === "string"
              ? result.payload
              : "Creation failed";
          notification.error("Creation Failed", errorMsg);
        }
      }
      setIsUnsavedChanges(false);
    } catch (error: any) {
      notification.error("Error", error.message || "An error occurred");
    }
  };

  const handleCancel = () => {
    if (isUnsavedChanges) {
      setIsCancelModalOpen(true);
    } else {
      onCancel();
    }
  };

  const confirmCancel = () => {
    setIsUnsavedChanges(false);
    reset();
    onCancel();
    setIsCancelModalOpen(false);
  };

  const handleFormSubmit = handleSubmit(handleSave);

  return (
    <>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <StyledForm
          layout="vertical"
          onFinish={handleFormSubmit}
        >
          <Form.Item
            label="Name"
            validateStatus={errors.name ? 'error' : ''}
            help={errors.name?.message}
            required
          >
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <StyledInput {...field} placeholder="Enter name" />
              )}
            />
          </Form.Item>

          <Form.Item
            label="Username"
            validateStatus={errors.username ? 'error' : ''}
            help={errors.username?.message}
            required
          >
            <Controller
              name="username"
              control={control}
              render={({ field }) => (
                <StyledInput {...field} placeholder="Enter username" />
              )}
            />
          </Form.Item>

          <Form.Item
            label="Email"
            validateStatus={errors.email ? 'error' : ''}
            help={errors.email?.message}
            required
          >
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <StyledInput {...field} type="email" placeholder="Enter email" />
              )}
            />
          </Form.Item>

          <Form.Item
            label="Personal Phone Number"
            validateStatus={errors.phone ? 'error' : ''}
            help={errors.phone?.message}
            required
          >
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <StyledInput {...field} placeholder="Enter personal phone number" />
              )}
            />
          </Form.Item>

          {effectiveType === 'merchant' && (
            <Form.Item
              label="Public Phone Number"
              validateStatus={errors.publicPhone ? 'error' : ''}
              help={errors.publicPhone?.message}
            >
              <Controller
                name="publicPhone"
                control={control}
                render={({ field }) => (
                  <StyledInput {...field} placeholder="Enter public phone number" />
                )}
              />
            </Form.Item>
          )}

          <Form.Item
            label="Address"
            validateStatus={errors.address ? 'error' : ''}
            help={errors.address?.message}
            required
          >
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <StyledTextArea {...field} rows={3} placeholder="Enter address" />
              )}
            />
          </Form.Item>

          {/* {isManagerMode && isEditMode && (
            <Form.Item
              label="User Role"
              validateStatus={errors.userRole ? 'error' : ''}
              help={errors.userRole?.message}
            >
              <Controller
                name="userRole"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <CustomSelector
                    value={value}
                    onChange={onChange}
                    options={MERCHANT_USER_ROLE_OPTIONS}
                    placeholder="Select user role"
                    allowClear
                    width="100%"
                  />
                )}
              />
            </Form.Item>
          )} */}

          {isEditMode && (
            <Form.Item label="Update Password">
              <Switch checked={showPasswordField} onChange={handlePasswordToggle} />
            </Form.Item>
          )}

          {(!isEditMode || showPasswordField) && (
            <Form.Item
              label="Password"
              validateStatus={errors.password ? 'error' : ''}
              help={errors.password?.message}
              required
            >
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <StyledPasswordInput
                    {...field}
                    placeholder="Enter password"
                    iconRender={(visible) => (
                      <span style={{ cursor: 'pointer' }}>
                        <FontAwesomeIcon icon={visible ? faEye : faEyeSlash} />
                      </span>
                    )}
                  />
                )}
              />
            </Form.Item>
          )}
        </StyledForm>
      </Space>

      <FormFooter>
        <CancelButton onClick={handleCancel}>Cancel</CancelButton>
        <SaveButton
          type="primary"
          htmlType="button"
          onClick={handleFormSubmit}
          loading={loading.create || loading.update}
          icon={<FontAwesomeIcon icon={faSave} />}
        >
          Save
        </SaveButton>
      </FormFooter>

      <StyledModal
        title="Unsaved Changes"
        open={isCancelModalOpen}
        onCancel={() => setIsCancelModalOpen(false)}
        footer={[
          <Button
            key="no"
            onClick={() => setIsCancelModalOpen(false)}
            style={{
              borderColor: "#d9d9d9",
              color: "#595959",
            }}
          >
            No
          </Button>,
          <Button
            key="yes"
            type="primary"
            danger
            onClick={confirmCancel}
            style={{
              background: "#ff4d4f",
              borderColor: "#ff4d4f",
            }}
          >
            Yes
          </Button>,
        ]}
      >
        <p>You have unsaved changes. Are you sure you want to cancel?</p>
      </StyledModal>
    </>
  );
};

export default MerchantBox;